import React, { useMemo, useState } from "react";
import { Filter, X, Trash2 } from "@/components/icons_new";

const BULK_ACTIONS = [
  "Request fulfillment",
  "Cancel fulfillment requests",
  "Mark as unfulfilled",
  "Change fulfillment location",
  "Archive orders",
  "Unarchive orders",
  "Cancel orders",
  "Add tags",
  "Remove tags",
];

export default function MultiColumnFilter({
  filterFields = [],
  selectedFilters = {},
  onChange,
  onClearAll,
  onBulkActionSelect,
  showBulkActions = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [activeFieldKey, setActiveFieldKey] = useState(null);

  const hasActiveFilters = useMemo(
    () =>
      Object.values(selectedFilters).some(
        (val) => Array.isArray(val) && val.length > 0
      ),
    [selectedFilters]
  );

  const activeField = filterFields.find((field) => field.key === activeFieldKey);

  const handleClearAll = () => {
    if (!hasActiveFilters) return;

    if (typeof onClearAll === "function") {
      onClearAll();
    } else {
      onChange({});
    }

    const url = new URL(window.location.href);
    ["status", "channel", "paymentStatus"].forEach((param) => url.searchParams.delete(param));
    window.history.replaceState({}, "", url.pathname);
  };

  return (
    <div className="relative inline-block text-left w-full">
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {/* Filter combo Chips */}
        {Object.entries(selectedFilters).map(([fieldKey, values]) => {
          if (!Array.isArray(values) || values.length === 0) return null;

          const field = filterFields.find((f) => f.key === fieldKey);
          const fieldLabel = field?.label || fieldKey;

          return values
            .filter(
              (val) =>
                typeof val === "string" &&
                val.trim().length > 1 &&
                !["l", "I", "A"].includes(val)
            )
            .map((val) => (
              <span
                key={`${fieldKey}-${val}`}
                className="
                  flex items-center gap-1 border border-solid border-[#b6bcff] text-xs rounded-full px-2 py-1
                  bg-gradient-to-b from-[#ffffff] to-[#edf2f9]
                  shadow-inner shadow-white/60
                "
              >
                <span className="text-[#000]">{fieldLabel}</span>
                <span className="text-[#333333] font-semibold">:</span>
                <span className="text-[#333333] font-semibold">{val}</span>
                <button
                  onClick={() => {
                    const updated = selectedFilters[fieldKey].filter((v) => v !== val);
                    onChange({
                      ...selectedFilters,
                      [fieldKey]: updated,
                    });
                  }}
                >
                  <X className="w-3 h-3 text-[#535353] hover:text-[#000]" />
                </button>
              </span>
            ));
        })}

        {/* Add Filter Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2 py-1 border border-dashed border-gray-400 text-[#333333] rounded text-sm font-medium bg-white hover:bg-[#eeeeee]"
        >
          <Filter className="w-4 h-4 text-[#333333]" />
          Add filter
        </button>

        {/* Bulk actions trigger */}
        {showBulkActions && (
          <div className="relative">
            <button
              onClick={() => setShowBulkMenu((prev) => !prev)}
              className="flex items-center gap-1 px-3 py-1 border border-gray-300 text-[#333333] rounded text-sm font-medium bg-white hover:bg-[#eeeeee]"
            >
              Bulk actions
            </button>

            {showBulkMenu && (
              <div className="absolute right-0 mt-2 w-56 rounded-lg border border-gray-200 bg-white shadow-lg z-50">
                <div className="py-1">
                  {BULK_ACTIONS.map((action) => (
                    <button
                      key={action}
                      onClick={() => {
                        setShowBulkMenu(false);
                        onBulkActionSelect?.(action);
                      }}
                      style={{ color: "#333333" }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100"
                    >
                      {action}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Clear All */}
        {hasActiveFilters && (
          <button
            onClick={handleClearAll}
            className="text-sm border border-dashed border-gray-400 px-2 py-1 rounded flex items-center gap-[0.5em] text-[#333333] font-medium bg-white hover:bg-[#eeeeee]"
          >
            <Trash2 className="w-4 h-4 text-[#333333]" />
            Clear all
          </button>
        )}
      </div>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-[200px] bg-white border border-gray-200 rounded shadow-lg">
          {/* Field Selector */}
          {!activeField && (
            <div className="relative max-h-64 overflow-y-auto">
              {/* Top bar */}
              <div className="flex justify-end items-center px-3 py-2 border-b">
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close filter menu"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* List of filter fields */}
              <div className="p-2">
                {filterFields.map((field) => (
                  <button
                    key={field.key}
                    onClick={() => setActiveFieldKey(field.key)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100"
                  >
                    {field.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Value Selector */}
          {activeField && (
            <div className="relative max-h-64 overflow-y-auto">
              {/* Top bar with field label and back */}
              <div className="flex justify-between items-center px-3 py-2 border-b">
                <span className="text-sm font-semibold">{activeField.label}</span>
                <button
                  onClick={() => setActiveFieldKey(null)}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Back to fields"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Value list */}
              <div className="p-2 flex flex-col gap-1">
                {activeField.options.map((option) => {
                  const isSelected =
                    selectedFilters[activeField.key]?.includes(option) ?? false;

                  return (
                    <label key={option} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const current = selectedFilters[activeField.key] || [];
                          const updated = e.target.checked
                            ? [...current, option]
                            : current.filter((val) => val !== option);
                          onChange({
                            ...selectedFilters,
                            [activeField.key]: updated,
                          });
                        }}
                      />
                      {option}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}